/** @ken/module-sales-receipts — frontend entry point.
 *
 * 0.2.0 — story-page detail (ADR-0081). The in-list Sheet is RETIRED for
 * sales receipts; the receipt-number cell is a stretched <Link> to the
 * full-page story route (sales-receipts/$id/overview), and the detail is a
 * parent shell + routed tab children (overview / line-items / related /
 * notes). No Payments tab — a sales receipt's tender IS the receipt.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { SalesReceiptStorySearch } from "./routes/story/sales-receipt-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadSalesReceiptStoryTabs = () => import("./routes/story/sales-receipt-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const SalesReceiptListPage = lazyScreen(
  () => import("./routes/sales-receipt-list"),
  "SalesReceiptListPage",
);
const SalesReceiptStoryDetail = lazyScreen(
  () => import("./routes/story/sales-receipt-story-detail"),
  "SalesReceiptStoryDetail",
);
const SalesReceiptActivityRoute = lazyScreen(
  loadSalesReceiptStoryTabs,
  "SalesReceiptActivityRoute",
);
const SalesReceiptLineItemsRoute = lazyScreen(
  loadSalesReceiptStoryTabs,
  "SalesReceiptLineItemsRoute",
);
const SalesReceiptNotesRoute = lazyScreen(
  loadSalesReceiptStoryTabs,
  "SalesReceiptNotesRoute",
);
const SalesReceiptOverviewRoute = lazyScreen(
  loadSalesReceiptStoryTabs,
  "SalesReceiptOverviewRoute",
);
const SalesReceiptRelatedRoute = lazyScreen(
  loadSalesReceiptStoryTabs,
  "SalesReceiptRelatedRoute",
);

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "sales-receipts",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof.
    validateSearch: (raw: Record<string, unknown>): SalesReceiptListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: SalesReceiptListPage,
  });

  // ── Story-page detail (ADR-0081): parent sales-receipts/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "sales-receipts/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (
      raw: Record<string, unknown>,
    ): SalesReceiptStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: SalesReceiptStoryDetail,
  });

  // Index redirect sales-receipts/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/sales-receipts/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: SalesReceiptOverviewRoute,
  });
  const lineItemsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "line-items",
    component: SalesReceiptLineItemsRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: SalesReceiptRelatedRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: SalesReceiptActivityRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: SalesReceiptNotesRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      lineItemsRoute,
      relatedRoute,
      activityRoute,
      notesRoute,
    ],
  );

  return [listRoute, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface SalesReceiptListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
